import React, { useState, useEffect } from "react";
import { DocumentDetailsResponse } from "../types/knowledge";
import "./DocumentDetailsModal.css";

interface DocumentDetailsModalProps {
  isOpen: boolean;
  documentId: string | null;
  token: string;
  tenantId: string;
  onClose: () => void;
}

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

const DocumentDetailsModal: React.FC<DocumentDetailsModalProps> = ({
  isOpen,
  documentId,
  token,
  tenantId,
  onClose,
}) => {
  const [details, setDetails] = useState<DocumentDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"info" | "chunks" | "content">(
    "info",
  );
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const [expandedTexts, setExpandedTexts] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && documentId) {
      fetchDocumentDetails();
    }
  }, [isOpen, documentId]);

  const fetchDocumentDetails = async () => {
    if (!documentId || !token) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/text-extraction/documents/${documentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch document details");
      }

      const data: DocumentDetailsResponse = await response.json();
      setDetails(data);
    } catch (err) {
      console.error("Error fetching document details:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load document details",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const toggleChunk = (chunkId: string) => {
    setExpandedChunks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(chunkId)) {
        newSet.delete(chunkId);
      } else {
        newSet.add(chunkId);
      }
      return newSet;
    });
  };

  const toggleText = (textId: string) => {
    setExpandedTexts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(textId)) {
        newSet.delete(textId);
      } else {
        newSet.add(textId);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Document Details</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">Loading document details...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : details ? (
            <>
              {/* Tabs */}
              <div className="tabs">
                <button
                  className={`tab ${activeTab === "info" ? "active" : ""}`}
                  onClick={() => setActiveTab("info")}
                >
                  Document Info
                </button>
                <button
                  className={`tab ${activeTab === "chunks" ? "active" : ""}`}
                  onClick={() => setActiveTab("chunks")}
                >
                  Chunks ({details.statistics.total_chunks})
                </button>
                <button
                  className={`tab ${activeTab === "content" ? "active" : ""}`}
                  onClick={() => setActiveTab("content")}
                >
                  Extracted Content
                </button>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === "info" && (
                  <div className="info-section">
                    <div className="info-grid">
                      <div className="info-item">
                        <label>Name:</label>
                        <span>{details.document.name}</span>
                      </div>
                      <div className="info-item">
                        <label>File Type:</label>
                        <span className="file-type-badge">
                          {details.document.file_type.toUpperCase()}
                        </span>
                      </div>
                      <div className="info-item">
                        <label>File Size:</label>
                        <span>
                          {formatFileSize(details.document.file_size)} (
                          {details.document.file_size_mb.toFixed(2)} MB)
                        </span>
                      </div>
                      <div className="info-item">
                        <label>Status:</label>
                        <span
                          className={`status-badge ${details.document.status}`}
                        >
                          {details.document.status}
                        </span>
                      </div>
                      <div className="info-item">
                        <label>Uploaded At:</label>
                        <span>{formatDate(details.document.uploaded_at)}</span>
                      </div>
                      <div className="info-item">
                        <label>Updated At:</label>
                        <span>{formatDate(details.document.updated_at)}</span>
                      </div>
                      <div className="info-item">
                        <label>Uploaded By:</label>
                        <span>
                          {details.uploaded_by.username} (
                          {details.uploaded_by.email})
                        </span>
                      </div>
                      <div className="info-item">
                        <label>File URL:</label>
                        <a
                          href={details.document.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-link"
                        >
                          Open File
                        </a>
                      </div>
                    </div>

                    <div className="statistics-section">
                      <h3>Statistics</h3>
                      <div className="stats-grid">
                        <div className="stat-box">
                          <label>Total Extracted Texts:</label>
                          <span className="stat-value">
                            {details.statistics.total_extracted_texts}
                          </span>
                        </div>
                        <div className="stat-box">
                          <label>Total Chunks:</label>
                          <span className="stat-value">
                            {details.statistics.total_chunks}
                          </span>
                        </div>
                        <div className="stat-box">
                          <label>Chunks with Embeddings:</label>
                          <span className="stat-value">
                            {details.statistics.chunks_with_embeddings}
                          </span>
                        </div>
                        <div className="stat-box">
                          <label>Chunks without Embeddings:</label>
                          <span className="stat-value">
                            {details.statistics.chunks_without_embeddings}
                          </span>
                        </div>
                        <div className="stat-box full-width">
                          <label>Embedding Progress:</label>
                          <div className="progress-container">
                            <div
                              className="progress-bar"
                              style={{
                                width: `${details.statistics.embedding_progress}%`,
                              }}
                            ></div>
                            <span className="progress-text">
                              {details.statistics.embedding_progress.toFixed(1)}
                              %
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "chunks" && (
                  <div className="chunks-section">
                    {details.chunks.length === 0 ? (
                      <div className="empty-state">No chunks available</div>
                    ) : (
                      <div className="chunks-list">
                        {details.chunks.map((chunk) => {
                          const isExpanded = expandedChunks.has(chunk.id);
                          return (
                            <div key={chunk.id} className="chunk-item">
                              <div className="chunk-header">
                                <span className="chunk-number">
                                  Chunk #{chunk.chunk_number}
                                </span>
                                <span
                                  className={`embedding-badge ${chunk.has_embedding ? "success" : "pending"}`}
                                >
                                  {chunk.has_embedding
                                    ? "✓ Has Embedding"
                                    : "No Embedding"}
                                </span>
                              </div>
                              <div className="chunk-meta">
                                <span>Words: {chunk.word_count}</span>
                                <span>Characters: {chunk.character_count}</span>
                                <span>
                                  Position: {chunk.start_position}-
                                  {chunk.end_position}
                                </span>
                              </div>
                              {chunk.has_embedding && (
                                <div className="chunk-embedding-info">
                                  <span>Model: {chunk.embedding_model}</span>
                                  <span>
                                    Created:{" "}
                                    {formatDate(chunk.embedding_created_at)}
                                  </span>
                                </div>
                              )}
                              <div className="chunk-content-wrapper">
                                <div className="chunk-content-header">
                                  <strong>Content:</strong>
                                  <button
                                    className="toggle-button"
                                    onClick={() => toggleChunk(chunk.id)}
                                  >
                                    {isExpanded ? "Show Less ▲" : "Show More ▼"}
                                  </button>
                                </div>
                                <p className="chunk-content">
                                  {isExpanded
                                    ? chunk.content
                                    : chunk.content_preview}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "content" && (
                  <div className="content-section">
                    {details.extracted_texts.length === 0 ? (
                      <div className="empty-state">
                        No extracted content available
                      </div>
                    ) : (
                      <div className="extracted-texts-list">
                        {details.extracted_texts.map((text) => {
                          const isExpanded = expandedTexts.has(text.id);
                          return (
                            <div key={text.id} className="extracted-text-item">
                              <div className="text-header">
                                <span className="extraction-type">
                                  {text.extraction_type}
                                </span>
                                <span className="quality-score">
                                  Quality:{" "}
                                  {(text.quality_score * 100).toFixed(0)}%
                                </span>
                              </div>
                              <div className="text-meta">
                                <span>Words: {text.word_count}</span>
                                <span>Characters: {text.character_count}</span>
                                <span>
                                  Processing Time:{" "}
                                  {text.processing_time.toFixed(3)}s
                                </span>
                                {text.extraction_metadata?.chunk_metadata && (
                                  <span>
                                    Chunks:{" "}
                                    {
                                      text.extraction_metadata.chunk_metadata
                                        .total_chunks
                                    }
                                  </span>
                                )}
                              </div>
                              <div className="text-content-wrapper">
                                <div className="text-content-header">
                                  <strong>Content:</strong>
                                  <button
                                    className="toggle-button"
                                    onClick={() => toggleText(text.id)}
                                  >
                                    {isExpanded ? "Show Less ▲" : "Show More ▼"}
                                  </button>
                                </div>
                                <pre className="text-content">
                                  {isExpanded
                                    ? text.content
                                    : text.content_preview}
                                </pre>
                              </div>
                              {text.extraction_metadata?.extraction_method && (
                                <div className="extraction-method">
                                  Method:{" "}
                                  {text.extraction_metadata.extraction_method}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailsModal;
